/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useEffect, useState } from "react";
import { GeneralDataItem, fetchFilteredDataByModel } from "../../../repo/api";
import { FilterQueryProps } from "../BulkUpdate";
import { Box, ThemeProvider, createTheme } from "@mui/material";
import { makeStyles, createStyles } from "@mui/styles";
import ErrorMessageBox from "../ErrorMessage";
import ProgressIndicator from "../../progressIndicator/ProgressIndicator";
import {
  MRT_RowSelectionState,
  MaterialReactTable,
} from "material-react-table";

interface SelectItemsProps<T> extends FilterQueryProps<T> {
  setSelectedItems: (items: MRT_RowSelectionState) => void;
  setTableData: (data: GeneralDataItem[]) => void;
  selectedItems: MRT_RowSelectionState;
  tableData: GeneralDataItem[];
}

interface Pagination {
  page: {
    limit: string;
    offset: string;
  };
  filter: { [index: string]: string | string[] };
  sort: string;
}

const itemPerPage = 25; // should be changed to 20 to get rid of the warning

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: "100%",
      overflow: "hidden",
    },
  })
);

const SelectItems = <T,>(props: SelectItemsProps<T>) => {
  const {
    filterQuery: { entity: model },
    filterQuery,
    tableColumns,
    tableData,
    selectedItems,
    setTableData,
    setSelectedItems,
  } = props;

  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  // Create theme inside component to avoid module-level issues
  const tableTheme = createTheme({
    palette: {
      secondary: {
        main: "#5494DC", // var(--dmg-blue-light)
        light: "#5494DC", // var(--dmg-blue-light)
        dark: "#5494DC", // var(--dmg-blue-light)
      },
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundColor: "var(--dmg-primary)",
            color: "#FFFFFF", // var(--dmg-bg)
            minHeight: 45,
            borderRadius: "none",
            borderTopLeftRadius: "4px!important",
            borderTopRightRadius: "4px!important",
            borderBottomLeftRadius: "0px!important",
            borderBottomRightRadius: "0px!important",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: "#fafafa!important", // var(--dmg-gray-100)
            fontSize: 14,
            fontWeight: 600,
          },
          body: {
            fontSize: 15,
          },
        },
      },
    },
  });

  const checkedAllOptions = (length: number) =>
    Object.fromEntries(Array.from({ length }, (_, i) => [i, true]));

  const getFilterData = (filter: Pagination["filter"]) => {
    const p: Pagination = {
      page: {
        limit: itemPerPage.toString(),
        offset: "0",
      },
      filter,
      sort: "",
    };
    return fetchFilteredDataByModel(model, p);
  };

  useEffect(() => {
    if (tableData.length === 0) {
      setIsLoading(true);
      getFilterData(filterQuery.values)
        .then((res) => {
          setTableData(res.data);
          setSelectedItems(checkedAllOptions(res.data.length)); // checked all options by default
          setIsLoading(false);
        })
        .catch((err) => {
          setIsError(true);
          // console.log(err);
        });
    }
  }, [filterQuery.values, tableData]);

  return (
    <Box className={classes.root}>
      {isError ? (
        <ErrorMessageBox message="Something wrong while getting the data. Please go back and try to filter data again!" />
      ) : isLoading && tableData.length === 0 ? (
        <ProgressIndicator />
      ) : (
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={tableColumns}
            data={tableData as T[]}
            initialState={{
              pagination: {
                pageIndex: 0,
                pageSize: itemPerPage,
              },
              showColumnFilters: false,
              density: "spacious",
            }}
            enableMultiRowSelection
            enableRowSelection
            onRowSelectionChange={setSelectedItems}
            muiTableBodyRowProps={({ row }) => ({
              onClick: row.getToggleSelectedHandler(),
              sx: {
                cursor: "pointer",
              },
            })}
            enableSelectAll
            selectAllMode="all"
            enableColumnActions={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableFilters={false}
            enableHiding={false}
            muiTablePaginationProps={{
              rowsPerPageOptions: [5, 10, 20, itemPerPage],
            }}
            state={{ rowSelection: selectedItems }}
          />
        </ThemeProvider>
      )}
    </Box>
  );
};

export default memo(SelectItems);
